import { useState } from 'react';
import { resetDatabase } from '@/db';
import { SyncAPI } from '@/lib/firebaseSync';
import { Trash2, AlertTriangle, Loader2, ShieldAlert } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const FIRST_PHRASE = 'APAGAR TUDO';
const SECOND_PHRASE = 'CONFIRMO';

/**
 * Card de RESET TOTAL — apaga banco local (IndexedDB) E todas as tabelas
 * sincronizadas no Firebase RTDB para o deviceGroup atual. Usado pra
 * eliminar lixo de testes antigos que volta a sincronizar e polui a
 * numeração de comandas em produção.
 *
 * Confirmação dupla obrigatória:
 *   Step 1: digitar "APAGAR TUDO"
 *   Step 2: digitar "CONFIRMO"
 */
export default function ResetTotalCard() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');

  const reset = () => {
    setStep(1);
    setConfirmText('');
    setBusy(false);
    setProgress('');
  };

  const handleClose = (next) => {
    if (busy) return;
    if (!next) reset();
    setOpen(!!next);
  };

  const handleNext = () => {
    const want = step === 1 ? FIRST_PHRASE : SECOND_PHRASE;
    if (confirmText.trim().toUpperCase() !== want) {
      toast.error(`Digite exatamente: ${want}`);
      return;
    }
    if (step === 1) {
      setStep(2);
      setConfirmText('');
      return;
    }
    void doReset();
  };

  const doReset = async () => {
    setBusy(true);
    try {
      setProgress('Apagando histórico no Firebase...');
      try {
        const wipe = await SyncAPI.wipeRemote();
        if (wipe?.ok) {
          // eslint-disable-next-line no-console
          console.log('[RESET] Firebase wipe OK:', wipe.deleted);
        } else {
          // eslint-disable-next-line no-console
          console.warn('[RESET] Firebase wipe pulado:', wipe?.reason);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RESET] Firebase wipe erro (ignorado):', err?.message || err);
      }
      setProgress('Apagando banco local...');
      await resetDatabase();
      setProgress('Pronto. Recarregando...');
      toast.success('Sistema zerado (local + Firebase)');
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      toast.error(err?.message || 'Falha ao resetar');
      setBusy(false);
      setProgress('');
    }
  };

  return (
    <>
      <div data-testid="reset-total-card" className="bg-white border-2 border-rose-200 rounded-2xl p-5 mt-6">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
            <ShieldAlert size={20} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-700">Zona perigosa</p>
            <h3 className="font-heading text-base font-black text-slate-900 mt-0.5">
              Reset Total do Sistema
            </h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Apaga <strong>todas as comandas, pedidos delivery e fila da cozinha</strong> no
              dispositivo <strong>e no Firebase</strong>. Use só pra limpar lixo de testes ou
              quando começar a operação do zero. Histórico de auditoria e configurações
              ficam preservados.
            </p>
            <button
              data-testid="open-reset-total-btn"
              onClick={() => setOpen(true)}
              className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 transition-colors"
            >
              <Trash2 size={15} /> Apagar tudo (preciso confirmar 2x)
            </button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent data-testid="reset-total-dialog" className="max-w-md bg-white border-2 border-rose-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-black text-rose-800 flex items-center gap-2">
              <AlertTriangle size={22} /> Reset Total — confirme {step} de 2
            </DialogTitle>
            <DialogDescription className="text-slate-700 leading-relaxed">
              {step === 1 ? (
                <>
                  Esta ação <strong>NÃO PODE SER DESFEITA</strong>. Vai apagar
                  comandas, delivery e fila da cozinha em <strong>todos os dispositivos</strong>
                  via Firebase.
                  <br /><br />
                  Para prosseguir, digite <strong className="font-mono">{FIRST_PHRASE}</strong> abaixo.
                </>
              ) : (
                <>
                  Última chance. Após este passo o reset começa imediatamente.
                  <br /><br />
                  Digite <strong className="font-mono">{SECOND_PHRASE}</strong> para finalizar.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <Input
            data-testid="reset-total-input"
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={step === 1 ? FIRST_PHRASE : SECOND_PHRASE}
            className="h-12 text-center text-lg font-mono font-bold tracking-wider border-2 border-rose-300 rounded-xl uppercase"
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNext();
            }}
          />

          {busy && progress && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 font-mono flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> {progress}
            </div>
          )}

          <DialogFooter className="gap-2">
            <button
              data-testid="reset-total-cancel"
              onClick={() => handleClose(false)}
              disabled={busy}
              className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              data-testid="reset-total-next"
              onClick={handleNext}
              disabled={busy || !confirmText}
              className="flex-1 h-11 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy
                ? <><Loader2 size={15} className="animate-spin" /> Apagando...</>
                : step === 1
                  ? 'Prosseguir'
                  : <>Apagar TUDO definitivamente</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
